import { X } from 'lucide-react';
import { PaginatedCombobox } from '@/components/ui/paginated-combobox';
import { Combobox } from '@/components/ui/combobox';
import { searchProjects, type ProjectSearchRow } from '@/lib/api/system/ingestion-observability';
import type { IngestionFilter } from '@/lib/api/system/ingestion-observability';
import { useIngestionManifest } from '../hooks/useIngestionManifest';

type FilterMode = 'scope' | 'events';

interface IngestionContextFiltersProps {
  filters: IngestionFilter;
  onFilterChange: (key: keyof IngestionFilter, value: string | null) => void;
  onClear: () => void;
  /** Controls which filters to show. 'scope' = site+camera only, 'events' = +outcome */
  mode?: FilterMode;
  /** Hide site/camera scope filters (used by embedded views where these are locked) */
  hideScope?: boolean;
}

export function IngestionContextFilters({
  filters,
  onFilterChange,
  onClear,
  mode = 'scope',
  hideScope = false,
}: IngestionContextFiltersProps) {
  const { outcomeOptions } = useIngestionManifest();

  const hasScopeFilters = !!(filters.projectId || filters.videoProcessorConfigId);
  const hasViewFilters = !!(filters.outcome);
  const hasActiveFilters = hasScopeFilters || hasViewFilters;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#2E3835] bg-white/[0.02] px-3 py-1.5">
      {!hideScope && (
        <>
          <span className="text-[10px] font-medium uppercase tracking-wider text-white/25">
            Scope
          </span>

          <PaginatedCombobox<ProjectSearchRow>
            queryKey={['projects', 'search']}
            fetchFn={searchProjects}
            getItemId={(p) => p.id}
            getItemLabel={(p) => p.name}
            value={filters.projectId}
            onSelect={(p) => onFilterChange('projectId', p?.id ?? null)}
            placeholder="All Sites"
            searchPlaceholder="Search sites..."
            className="w-[280px] [&_button]:border-[#2E3835] [&_button]:bg-transparent [&_button]:text-sm [&_button]:text-white/60 [&_button]:shadow-none"
          />
          <input
            value={filters.videoProcessorConfigId ?? ''}
            onChange={(e) => onFilterChange('videoProcessorConfigId', e.target.value || null)}
            placeholder="Camera (VPC ID)"
            className="h-8 w-[200px] rounded-md border border-[#2E3835] bg-transparent px-2 text-sm text-white/60 placeholder:text-white/30 outline-none focus:border-white/20"
          />
        </>
      )}

      {(mode === 'events') && (
        <>
          {!hideScope && (
            <div className="mx-1 h-4 w-px bg-white/10" />
          )}

          <Combobox
            options={outcomeOptions}
            value={filters.outcome}
            onSelect={(v) => onFilterChange('outcome', v || null)}
            placeholder="Outcome"
            searchPlaceholder="Search..."
            className="w-[160px] [&_button]:border-[#2E3835] [&_button]:bg-transparent [&_button]:text-sm [&_button]:text-white/60 [&_button]:shadow-none"
          />
        </>
      )}

      {hasActiveFilters && (
        <button
          onClick={onClear}
          className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/60 transition-colors hover:bg-white/20 hover:text-white"
        >
          <X className="h-2.5 w-2.5" />
          Clear
        </button>
      )}
    </div>
  );
}
